import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import sounds from 'src/lib/sounds';
import * as Social from 'src/replicant/social';
import StateObserver, { user } from 'src/StateObserver';
import { createWaiter } from 'src/lib/utils';
import { duration } from 'src/replicant/utils/duration';
import { formatDuration } from 'src/lib/formatDuration';
import { startInviteFriendsSequence } from '../social/social.sequence';
import { Feature } from 'src/replicant/analytics';
import PopupBasicLazy from '../components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';
import { getCurrentChallengeWallpaper } from 'src/replicant/social/social.getters';

export default class PopupInviteChallenge extends PopupBasicLazy {
  constructor(opts: { superview: View; close: () => void }) {
    const locale = StateObserver.getState().ui.locale === 'en' ? 'en' : 'ja';
    const isJa = locale === 'ja';
    super({
      superview: opts.superview,
      close: opts.close,
      zIndex: 10000,
      height: 951,
      width: isJa ? 692 : 684,
      tracking: {
        feature: Feature.INVITE._,
        $subFeature: 'popup-invite-challenge',
      },
      skipTitle: true,
    });

    this.box.updateOpts({
      image: `assets/ui/inviteChallenge/${getCurrentChallengeWallpaper(
        user(),
      )}/bg_${locale}.png`,
    });

    this.message.updateOpts({
      width: this.box.style.width - 80,
      height: 130,
      x: 34,
      y: isJa ? 478 : 468,
      size: 31,
      color: 'white',
      zIndex: 1,
    });

    new LangBitmapFontTextView({
      superview: this.box,
      size: 24,
      font: 'Title',
      height: 60,
      width: 220,
      x: isJa ? 108 : 94,
      y: 624,
      verticalAlign: 'center',
      align: 'center',
      color: 'white',
      localeText: () => i18n('social.inviteChallenge.intro.timeRemaining'),
    });

    new LangBitmapFontTextView({
      superview: this.box,
      size: 24,
      font: 'Title',
      height: 60,
      width: 220,
      x: this.box.style.width - (isJa ? 334 : 324),
      y: 624,
      verticalAlign: 'center',
      align: 'center',
      color: 'white',
      localeText: () => i18n('social.inviteChallenge.intro.invitesRemaining'),
    });

    const state = user();
    const inviteChallenge = state.social.challenge;
    const remainingInvites = Math.max(
      Social.getters.getInviteChallengeTarget(state) - inviteChallenge.counter,
      0,
    );

    new LangBitmapFontTextView({
      superview: this.box,
      size: 37,
      font: 'Body',
      height: 60,
      width: 60,
      x: this.box.style.width - 236,
      y: isJa ? 682 : 680,
      verticalAlign: 'center',
      align: 'center',
      color: '#2CB216',
      localeText: () => remainingInvites.toString().padStart(2, '0'),
    });

    const timerText = new LangBitmapFontTextView({
      superview: this.box,
      size: 37,
      font: 'Body',
      height: 60,
      width: 220,
      x: isJa ? 108 : 94,
      verticalAlign: 'center',
      align: 'center',
      y: 680,
      color: '#754111',
      localeText: () => '',
    });

    const endTime =
      inviteChallenge.startTime + Social.ruleset.inviteChallengeDuration;
    const updateTimer = () => {
      const remainingTime = endTime - StateObserver.now();

      timerText.localeText = () => formatDuration(Math.max(remainingTime, 0));

      if (remainingTime > 0) {
        createWaiter().wait(updateTimer, duration({ seconds: 1 }));
      }
    };

    updateTimer();

    new LangBitmapFontTextView({
      superview: this.box,
      size: 20,
      font: 'Body',
      height: 100,
      width: this.box.style.width - 150,
      x: 75,
      y: 780,
      verticalAlign: 'center',
      align: 'center',
      color: '#754111',
      localeText: () => i18n('social.inviteChallenge.intro.rewardDetails'),
    });

    new ButtonScaleViewWithText({
      superview: this.box,
      ...uiConfig.buttons.secondary,
      labelOffsetX: -4,
      labelOffsetY: -1,
      fontSize: 38,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2,
      y: this.box.style.height + 20 - 80 / 2,
      width: 229,
      height: 80,
      centerOnOrigin: true,
      localeText: () => i18n('basic.ok'),
      onClick: async () => {
        // Hide the popup but don't call the close callback until the invite sequence
        // has completed. This ensures the launch sequence steps run sequentially
        sounds.playSound('ok');
        this.fadeOut();

        await startInviteFriendsSequence(
          Feature.INVITE._,
          Feature.INVITE.WALLPAPER_REWARD,
        );
        opts.close();
      },
      tracking: {
        feature: Feature.INVITE._,
        $subFeature: 'popup-invite-challenge-ok',
      },
    });

    this.buttonClose.updateOpts({
      x: this.box.style.width - 60,
      y: 73,
    });
    this.buttonClose.tracking = {
      feature: 'invite',
      $subFeature: 'popup-invite-challenge-cancel',
    };
  }

  protected getAssetsGroup(): AssetGroup | null {
    const locale = StateObserver.getLocale();
    const wallpaper = getCurrentChallengeWallpaper(user());

    return new AssetGroup([
      `assets/ui/inviteChallenge/${wallpaper}/bg_${locale}.png`,
    ]);
  }

  public init() {
    super.init({
      message: i18n('social.inviteChallenge.intro.message', {
        target: Social.getters.getInviteChallengeTarget(user()),
      }),
    });
  }
}
